// react-router components
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import { Box, Divider, Grid, Icon, Collapse, Card, Skeleton } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftBadge from "components/SoftComponents/SoftBadge";

import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';

import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";

import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

import badgeDict from "modules/inspections/components/badgeDict.json";
import SprayCan from "assets/images/spray_can_white.png";
import BrokenDevice from "assets/images/broken_device_white.png";

import { useAuth0 } from "@auth0/auth0-react";
import inspectionsAPI from "modules/inspections/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSoftUIController } from "hooks/context";
import Swal from "sweetalert2";

import { getStatusColour, calculateTimeDiff, determineReportingDate } from "utils/inspection";
import { format } from "date-fns";

function InspectionPreviewCard({ user, type, selectedInspection, setSelectedInspection }) {
    const auth0Context = useAuth0();
    useEffect(() => {
        inspectionsAPI.loadToken(auth0Context);
    }, [auth0Context]);

    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions } = controller;

    const canAssignmentsAdd = userPermissions.includes('operations-assignments:add');
    const canOperationsEdit = userPermissions.includes('operations-inspections:edit') || userPermissions.includes('operations-assignments:edit');
    const canOperationsDelete = userPermissions.includes('operations-inspections:delete') || userPermissions.includes('operations-assignments:delete');

    const showUnderReview = userPermissions.includes('operations:reviewer');
    const canViewThread = selectedInspection != null && type !== 'inspection_thread' && ((selectedInspection?.ascendant_tree?.length || 0) + (selectedInspection?.descendant_tree?.length || 0)) >= 1;
    const isThread = type === "inspection_thread";

    // Accordion states
    const [showPast, setShowPast] = useState(false);
    const [showFuture, setShowFuture] = useState(false);
    const [clickState, setClickState] = useState(null);

    const derivedStatus = useMemo(() => {
        let adjustedStatus = selectedInspection?.status || selectedInspection?.ascendant_tree?.[0]?.record?.status || null;
        if (!showUnderReview && adjustedStatus === "review") {
            adjustedStatus = "resolved";
        }
        return adjustedStatus;
    }, [selectedInspection, showUnderReview]);

    const trueStatus = useMemo(() => {
        return selectedInspection?.status || selectedInspection?.ascendant_tree?.[0]?.record?.status || null;
    }, [selectedInspection]);

    const locationContainer = useRef(null);
    const locationMap = useRef(null);
    const locationMarker = useRef(null); // Track marker to move it without rebuilding map
    mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

    const queryClient = useQueryClient();

    const editStatusMutation = useMutation({
        mutationFn: (id) => inspectionsAPI.updateInspectionStatus(id),
        onMutate: async (id) => {
            await queryClient.cancelQueries({ queryKey: ['getAllInspectionsForUser'] });
            const previousInspections = queryClient.getQueryData(['getAllInspectionsForUser', user, selectedOrg]);

            queryClient.setQueriesData(
                { queryKey: ['getAllInspectionsForUser'] },
                (old) => {
                    if (!old) return old;
                    const listKey = Array.isArray(old) ? null : (old.inspections ? 'inspections' : null);
                    const list = listKey ? old[listKey] : old;

                    if (!Array.isArray(list)) return old;

                    const updatedList = list.map((insp) => insp._id === id ? { ...insp, status: 'resolved' } : insp);
                    return listKey ? { ...old, [listKey]: updatedList } : updatedList;
                }
            );

            setSelectedInspection((prev) => prev ? { ...prev, status: 'resolved' } : prev);
            return { previousInspections };
        },
        onSuccess: () => {
            Swal.fire({ icon: 'success', title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px;">Inspection Resolved!</div>`, toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
        },
        onError: (err, id, context) => {
            if (context?.previousInspections) queryClient.setQueryData(['getAllInspectionsForUser', user, selectedOrg], context.previousInspections);
            Swal.fire({ icon: 'error', title: 'Update failed', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['getAllInspectionsForUser'] });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => inspectionsAPI.deleteInspection(id),
        onMutate: async (id) => {
            await queryClient.cancelQueries({ queryKey: ['getAllInspectionsForUser', user, selectedOrg] });
            const previousInspections = queryClient.getQueryData(['getAllInspectionsForUser', user, selectedOrg]);

            queryClient.setQueryData(['getAllInspectionsForUser', user, selectedOrg], (old) => {
                if (!old?.inspections) return old;
                return { ...old, inspections: old.inspections.filter(insp => insp._id !== id) };
            });

            setClickState(null);
            setSelectedInspection(null);
            return { previousInspections };
        },
        onSuccess: () => {
            Swal.fire({ icon: 'success', title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px; text-transform:capitalize;">${type.split('_')[0]} Deleted!</div>`, toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
        },
        onError: (err, id, context) => {
            queryClient.setQueryData(['getAllInspectionsForUser', user, selectedOrg], context.previousInspections);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['getAllInspectionsForUser', user, selectedOrg] });
        }
    });

    const handleDelete = (id) => {
        if (clickState === id) deleteMutation.mutate(id);
        else setClickState(id);
    };

    useEffect(() => {
        const loc = selectedInspection?.location || selectedInspection?.ascendant_tree?.[0]?.record?.location;
        if (!locationContainer.current || !loc || !loc.length) return;

        let timeoutId;

        if (!locationMap.current) {
            // First time opening the card ever: Wait 100ms for UI to paint, then build map
            timeoutId = setTimeout(() => {
                if (!locationContainer.current) return;

                locationMap.current = new mapboxgl.Map({
                    container: locationContainer.current,
                    style: 'mapbox://styles/mapbox/dark-v11',
                    center: [loc[1], loc[0]],
                    zoom: 13,
                    interactive: false,
                });

                locationMarker.current = new mapboxgl.Marker({ color: '#ff0000' })
                    .setLngLat([loc[1], loc[0]])
                    .addTo(locationMap.current);
            }, 100);
        } else {
            // Map exists and container was reused! Instantly fly to the new coordinates.
            locationMap.current.jumpTo({ center: [loc[1], loc[0]], zoom: 15 });
            if (locationMarker.current) {
                locationMarker.current.setLngLat([loc[1], loc[0]]);
            }
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [selectedInspection?._id, selectedInspection?.location, selectedOrg]);

    // Clean up WebGL map only when the ENTIRE card unmounts
    useEffect(() => {
        return () => {
            if (locationMap.current) {
                locationMap.current.remove();
                locationMap.current = null;
                locationMarker.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (selectedInspection && selectedInspection.organisation_id !== selectedOrg) {
            setSelectedInspection(null);
        }
    }, [selectedOrg]);

    if (selectedInspection == null) return null;

    const hasAncestors = selectedInspection.ascendant_tree?.length > 0;
    const hasDescendants = selectedInspection.descendant_tree?.length > 0;

    const renderNode = (node, isCurrent, isLastItem, key) => {
        const bgColour = isCurrent ? 'transparent' : '#f1f1f1';
        const paddingLayout = isCurrent && deviceLayout === 'mobile' ? 0.5 : 1;

        let nodeTitle = node.record.title;
        if (!isCurrent) {
            const isRoot = !node.record.inspection_record_id;
            nodeTitle = isRoot ? `Original Inspection: ${node.record.title}` : `Rectification: ${node.record.title}`;
        }

        var { timescale, label, reported_date, timer_date, tag, overdue } = determineReportingDate({ inspectionData: { ...node.record }, status: derivedStatus }, selectedOrg);

        const recDate = format(new Date(reported_date * 1000), "EEE, dd MMM yyyy, hh:mm a");

        return (
            <SoftBox key={key} mt={hasAncestors ? 0 : 4} mb={!isCurrent && !isLastItem ? 2 : 0} py={isCurrent ? 0 : 1} px={isCurrent ? 0 : (deviceLayout === 'mobile' ? 1 : 0)} sx={{ backgroundColor: bgColour, borderRadius: '10px' }}>
                <SoftBox p={paddingLayout}>
                    <Grid container spacing={2}>
                        <Grid item xxs={isCurrent ? 7 : 12}>
                            <SoftBox px={isCurrent ? 0 : (deviceLayout === 'mobile' ? 0 : 1)}>
                                <SoftBox mb={1} pr={5}>
                                    <SoftTypography variant={deviceLayout === "mobile" ? "h5" : (isCurrent ? "h3" : "h4")} fontWeight="bold" textTransform="capitalize">
                                        {nodeTitle}
                                    </SoftTypography>
                                </SoftBox>

                                {isCurrent && (
                                    <SoftBox display="flex" flexDirection={{ xxs: "column", sm: "row" }} gap={1} alignItems={{ xxs: "flex-start", sm: "center" }} mb={1}>
                                        {derivedStatus && (
                                            <SoftBadge variant="contained" color={getStatusColour(derivedStatus)} badgeContent={derivedStatus} container />
                                        )}
                                        {timescale == "future" && (
                                            <SoftBadge variant="contained" color={tag.color} badgeContent={overdue ? 'overdue' : `scheduled (${calculateTimeDiff(reported_date)})`} container />
                                        )}
                                        {node.record?.maintenance_tag && (
                                            <SoftBadge variant="contained" color={node.record.maintenance_tag.colour} badgeContent={node.record.maintenance_tag.tag_label} container />
                                        )}
                                    </SoftBox>
                                )}

                                <SoftBox mt={2}>
                                    <SoftTypography variant="button" fontWeight="bold">Date {label}</SoftTypography>
                                </SoftBox>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography variant={"body4"} fontWeight="medium">{recDate}</SoftTypography>
                                </SoftBox>
                                <SoftBox mt={3} mb={1}>
                                    <SoftTypography variant="button" fontWeight="bold">Additional Information</SoftTypography>
                                </SoftBox>
                                <SoftBox pl={0.5} pr={5}>
                                    <SoftTypography variant="body2" color="text">{node.record.description ? node.record.description : "No additional information added."}</SoftTypography>
                                </SoftBox>
                            </SoftBox>
                        </Grid>

                        {isCurrent && (
                            <Grid item xxs={5} lg={5}>
                                {/* Permanent Background Skeleton covered seamlessly by Mapbox */}
                                <Card sx={{ width: '100%', height: '225px', position: 'relative' }}>
                                    <Skeleton variant="rectangular" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }} />
                                    <Box component="div" ref={locationContainer} sx={{ height: '100%', position: 'relative', zIndex: 1 }} />
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </SoftBox>

                {node.devices.map((device, deviceIndex) => {
                    const deviceImages = node.images.find(obj => obj.uuid === device.uuid);

                    return (
                        <Box key={deviceIndex}>
                            <SoftBox p={deviceLayout === 'mobile' ? 0 : (isCurrent ? 0.5 : 1)} pt={deviceLayout === 'mobile' ? 0.5 : (isCurrent ? 0.5 : 1)}>
                                <Divider />
                                <SoftBox my={isCurrent ? 2 : 1.5} mx={isCurrent ? 0 : 1} display="flex" justifyContent="space-between" alignItems="center" gap={0.5}>
                                    <SoftTypography variant="subtitle1" fontWeight="bold">Site Details {node.devices.length > 1 ? (!deviceIndex ? "(Original)" : "(Replacement)") : ""}</SoftTypography>
                                    <SoftButton variant="gradient" component={Link} to={`/devices/${device.uuid}`} color="secondary" sx={{ padding: '0.5rem 0.75rem !important', minHeight: '0 !important', minWidth: '0 !important', borderRadius: '6px' }}><SoftTypography pr={1} color="white" variant='caption'>More Info</SoftTypography><Icon>arrow_forward</Icon></SoftButton>
                                </SoftBox>
                                <SoftBox py={1.5} px={1} mx={isCurrent ? 0 : 1} sx={{ backgroundColor: '#f9f9f9', borderRadius: '10px' }}>
                                    <Grid container>
                                        <Grid item xxs={12} lg={5}>
                                            <SoftBox mb={0.5} ml={0.5}>
                                                <SoftTypography variant="button" fontWeight="bold">Device</SoftTypography>
                                            </SoftBox>
                                            <SoftBox ml={1} display="flex" gap={0.5}>
                                                <SoftTypography variant="subtitle3" color="text">
                                                    {`${selectedInspection.organisation_id === 'clearways' ? 'eMinor' : 'Name'}:`}
                                                </SoftTypography>
                                                <SoftTypography variant="subtitle3" color="dark">
                                                    {device.name}
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftBox ml={1} display="flex" gap={0.5}>
                                                <SoftTypography variant="subtitle3" color="text">
                                                    {selectedInspection.organisation_id === 'clearways' ? `Site ID:` : `Stop ID:`}
                                                </SoftTypography>
                                                <SoftTypography variant="subtitle3" color="dark">
                                                    {device.stop_id}
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftBox ml={1} mb={1} display="flex" gap={0.5}>
                                                <SoftTypography variant="subtitle3" color="text">Serial No:</SoftTypography>
                                                <SoftTypography variant="subtitle3" color="dark">{device.serial_no}</SoftTypography>
                                            </SoftBox>
                                        </Grid>
                                        <Grid item xxs={6} lg={3.5} mt={{ xxs: 2, lg: 0 }}>
                                            <SoftBox mb={0.5} ml={0.5}>
                                                <SoftTypography variant="button" fontWeight="bold">Device Conditions</SoftTypography>
                                            </SoftBox>
                                            {(device.conditions && Object.entries(device.conditions).filter((row) => row[1] === true || (row[0] === "other" && row[1] !== "")).length > 0) ? (
                                                Object.entries(device.conditions).filter((row) => row[1] === true || (row[0] === "other" && row[1] !== "")).map(([key, value], conIndex) => {
                                                    const borderSize = "20px";
                                                    const fontSize = "18px";

                                                    return (
                                                        <SoftBox key={conIndex} pb={0.25}>
                                                            <SoftBox pl={0.5} pb={key == "other" ? 0 : 0.25} display="flex" flexDirection="row" alignItems="center">
                                                                {badgeDict[key].icon == "SprayCan" ? (
                                                                    <SoftBox component="img" src={SprayCan} variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: "4px", width: borderSize, height: borderSize }} />
                                                                ) : badgeDict[key].icon == "BrokenDevice" ? (
                                                                    <SoftBox component="img" src={BrokenDevice} variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: "4px", width: borderSize, height: borderSize }} />
                                                                ) : (
                                                                    <SoftBox variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: "4px", width: borderSize, height: borderSize }}>
                                                                        <Icon fontSize={`${fontSize} !important`} sx={{ color: "#fff", fontSize: fontSize, display: "flex", alignItems: "center", justifyContent: "center", width: `${borderSize} !important`, height: `${borderSize} !important` }}>{badgeDict[key].icon}</Icon>
                                                                    </SoftBox>
                                                                )}
                                                                <SoftTypography pl={1} variant={"subtitle3"} textTransform="capitalize">{key.replace("_", " ")}</SoftTypography>
                                                            </SoftBox>
                                                            {key == "other" && (
                                                                <SoftBox pl={4}>
                                                                    <SoftTypography variant="caption">{value}</SoftTypography>
                                                                </SoftBox>
                                                            )}
                                                        </SoftBox>
                                                    );
                                                })
                                            ) : (
                                                <SoftBox ml={1}>
                                                    <SoftTypography variant="subtitle3" color="text">None listed.</SoftTypography>
                                                </SoftBox>
                                            )}
                                        </Grid>
                                        <Grid item xxs={6} lg={3.5} mt={{ xxs: 2, lg: 0 }}>
                                            <SoftBox mb={0.5} ml={0.5}>
                                                <SoftTypography variant="button" fontWeight="bold">Technician Actions</SoftTypography>
                                            </SoftBox>
                                            {(device.actions && Object.entries(device.actions).filter((row) => row[1] === true || (row[0] === "other" && row[1] !== "")).length > 0) ? (
                                                Object.entries(device.actions).filter((row) => row[1] === true || (row[0] === "other" && row[1] !== "")).map(([key, value], actIndex) => {
                                                    const borderSize = "20px";
                                                    const fontSize = "18px";

                                                    return (
                                                        <SoftBox pb={0.25} key={actIndex}>
                                                            <SoftBox pl={0.5} pb={key == "other" ? 0 : 0.25} display="flex" flexDirection="row" alignItems="center">
                                                                <SoftBox variant="gradient" bgColor={"success"} sx={{ borderRadius: "4px", width: borderSize, height: borderSize }}>
                                                                    <Icon fontSize={`${fontSize} !important`} sx={{ color: "#fff", fontSize, display: "flex", alignItems: "center", justifyContent: "center", width: `${borderSize} !important`, height: `${borderSize} !important` }}>check</Icon>
                                                                </SoftBox>
                                                                <SoftTypography pl={1} variant={"subtitle3"} textTransform="capitalize">{key.replace("_", " ")}</SoftTypography>
                                                            </SoftBox>
                                                            {key == "other" && (
                                                                <SoftBox pl={4}>
                                                                    <SoftTypography variant="caption">{value}</SoftTypography>
                                                                </SoftBox>
                                                            )}
                                                        </SoftBox>
                                                    );
                                                })
                                            ) : (
                                                <SoftBox ml={1}>
                                                    <SoftTypography variant="subtitle3" color="text">None listed.</SoftTypography>
                                                </SoftBox>
                                            )}
                                        </Grid>
                                    </Grid>
                                </SoftBox>

                                {deviceImages?.urls?.length > 0 ? (
                                    <SoftBox px={isCurrent ? 0 : (deviceLayout === 'mobile' ? 0 : 1)} mt={(device.conditions || device.actions) ? 2 : 1} pb={isCurrent ? 0 : 1}>
                                        <SoftBox p={1} mx={isCurrent ? 0 : 1} sx={{ backgroundColor: '#f9f9f9', borderRadius: '10px' }}>
                                            <PhotoProvider key={`${node.record._id}-${device.uuid}`}>
                                                <Grid container spacing={1}>
                                                    {deviceImages.urls.map((img, imgIndex) => (
                                                        <Grid item key={imgIndex} xxs={4} lg={3} x3l={2}>
                                                            <PhotoView src={img}>
                                                                <SoftBox
                                                                    component="img"
                                                                    src={img}
                                                                    width="100%"
                                                                    height="200px"
                                                                    sx={{
                                                                        objectFit: 'cover',
                                                                        borderRadius: '5px',
                                                                        cursor: 'pointer',
                                                                        transition: "box-shadow 0.3s ease-in-out",
                                                                        "&:hover": { boxShadow: "0 0 15px 2px rgba(0, 0, 0, 0.3)" },
                                                                        backgroundColor: '#e0e0e0' // Native fallback color before image loads
                                                                    }}
                                                                />
                                                            </PhotoView>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </PhotoProvider>
                                        </SoftBox>
                                    </SoftBox>
                                ) : (
                                    <SoftBox px={isCurrent ? 0 : (deviceLayout === 'mobile' ? 0 : 1)} mt={(device.conditions || device.actions) ? 2 : 1} pb={isCurrent ? 0 : 1}>
                                        <SoftBox p={1} sx={{ backgroundColor: '#f9f9f9', borderRadius: '10px' }}>
                                            <SoftTypography variant="body3" fontWeight="regular">No site images to show.</SoftTypography>
                                        </SoftBox>
                                    </SoftBox>
                                )}
                            </SoftBox>
                        </Box>
                    );
                })}
            </SoftBox>
        );
    };

    function devicesSorter(a, b) {
        if (a.actions.installed && !b.actions.installed) {
            return 1;
        }
        else if (!a.actions.installed && b.actions.installed) {
            return -1;
        }
        else {
            return 0;
        }
    }

    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <SoftBox sx={{ flexGrow: 1, overflowY: 'auto', p: 1, display: 'flex', flexDirection: 'column' }}>
                <SoftBox sx={{ flexGrow: 1 }}>
                    <SoftBox
                        sx={{
                            position: 'absolute',
                            left: '8px',
                            top: '8px',
                            verticalAlign: 'center',
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            border: '1px solid #bbbbbb',
                            backgroundColor: 'rgba(221, 221, 221, 0.75)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.25)',
                            px: 1,
                            py: 0.3,
                            borderRadius: '10px',
                            cursor: 'pointer',
                            zIndex: 10,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: 'rgba(210, 210, 210, 0.8)',
                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)',
                            }
                        }}
                        onClick={() => {
                            setSelectedInspection(null);
                            setClickState(null);
                        }}
                    >
                        <SoftTypography variant="button" verticalAlign="center">Close</SoftTypography>
                    </SoftBox>

                    {isThread && (
                        <SoftBox display="flex" justifyContent="flex-end" width="calc(100% - 60px - 8px)" pt={{ xxs: 1, xl: 0.5 }} pb={0.5} pl={'60px'} pr={1}>
                            <SoftTypography variant="mobileBody2" color="text" sx={{ textTransform: 'capitalize', textAlign: 'right' }}>
                                <i>'<b>{selectedInspection?.ascendant_tree?.[0]?.record?.title || selectedInspection.title}</b>' Thread</i>
                            </SoftTypography>
                        </SoftBox>
                    )}

                    {hasAncestors && (
                        <SoftBox mt={isThread ? 2 : 4.5} mb={2}>
                            <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                onClick={() => setShowPast(!showPast)}
                                p={1.5}
                                sx={{
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: showPast ? '8px 8px 0px 0px' : '8px',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    '&:hover': { backgroundColor: '#e9ecef' }
                                }}
                            >
                                <SoftTypography variant="button" fontWeight="bold" color="text">
                                    {showPast ? 'Hide Past History' : 'Show Past History'} ({selectedInspection.ascendant_tree.length})
                                </SoftTypography>
                                <Icon color="secondary">{showPast ? 'expand_less' : 'expand_more'}</Icon>
                            </SoftBox>
                            <Collapse in={showPast} unmountOnExit>
                                <SoftBox p={1} sx={{ backgroundColor: '#f8f9fa', borderRadius: '0 0 8px 8px' }}>
                                    {selectedInspection.ascendant_tree.map((node, i) => renderNode({ ...node, devices: node.devices.sort(devicesSorter), relation: 'ancestor' }, false, (selectedInspection.ascendant_tree.length - 1) === i, `past-${node.record._id}`))}
                                </SoftBox>
                            </Collapse>
                        </SoftBox>
                    )}

                    {renderNode({
                        record: selectedInspection,
                        devices: selectedInspection.devices.sort(devicesSorter) || [],
                        images: selectedInspection.images || [],
                        relation: 'current'
                    }, true, false, `current-inspection`)}

                    {hasDescendants && (
                        <SoftBox mt={2}>
                            <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                onClick={() => setShowFuture(!showFuture)}
                                p={1.5}
                                sx={{
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: showFuture ? '8px 8px 0px 0px' : '8px',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    '&:hover': { backgroundColor: '#e9ecef' }
                                }}
                            >
                                <SoftTypography variant="button" fontWeight="bold" color="text">
                                    {showFuture ? 'Hide Future History' : 'Show Future History'} ({selectedInspection.descendant_tree.length})
                                </SoftTypography>
                                <Icon color="secondary">{showFuture ? 'expand_less' : 'expand_more'}</Icon>
                            </SoftBox>
                            <Collapse in={showFuture} unmountOnExit>
                                <SoftBox p={1} sx={{ backgroundColor: '#f8f9fa', borderRadius: '0 0 8px 8px' }}>
                                    {selectedInspection.descendant_tree.map((node, i) => renderNode({ ...node, devices: node.devices.sort(devicesSorter), relation: 'descendant' }, false, (selectedInspection.descendant_tree.length - 1) === i, `future-${node.record._id}`))}
                                </SoftBox>
                            </Collapse>
                        </SoftBox>
                    )}
                </SoftBox >

                <Box my={2}>
                    <Divider />
                </Box>
                <SoftBox>
                    <Box p={1} sx={{ background: "#dcdcdc", borderRadius: "10px" }}>
                        <SoftBox
                            display="flex"
                            flexDirection={{ xxs: "column", md: "row" }}
                            gap={1}
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <SoftBox
                                display="flex"
                                flexWrap={{ xxs: "wrap", md: "nowrap" }}
                                flexGrow={1}
                                width={{ xxs: "100%", md: "auto" }}
                                gap={1}
                                justifyContent="flex-start"
                                alignItems="center"
                            >
                                {canAssignmentsAdd &&
                                    (trueStatus === "review" || trueStatus === "unresolved") && (
                                        <SoftBox display="flex" flexGrow={{ xxs: 1, md: 0 }}>
                                            <SoftButton
                                                variant="contained"
                                                color="primary"
                                                component={Link}
                                                disabled={editStatusMutation.isPending}
                                                to={`/operations/assignments/${selectedInspection.descendant_tree.length == 0
                                                    ? selectedInspection._id
                                                    : selectedInspection.descendant_tree[
                                                        selectedInspection.descendant_tree.length - 1
                                                    ].record._id
                                                    }`}
                                                sx={{
                                                    minHeight: "2.5rem !important",
                                                    height: "2.5rem !important",
                                                    width: { xxs: "100%", md: "auto" }
                                                }}
                                            >
                                                Add an Update
                                            </SoftButton>
                                        </SoftBox>
                                    )}

                                {canOperationsEdit &&
                                    (derivedStatus === "review" || derivedStatus === "unresolved") && (
                                        <SoftBox display="flex" flexGrow={{ xxs: 1, md: 0 }}>
                                            <SoftButton
                                                variant="contained"
                                                color="dark"
                                                onClick={() =>
                                                    editStatusMutation.mutate(
                                                        selectedInspection.ascendant_tree?.[0]?.record?._id ||
                                                        selectedInspection._id
                                                    )
                                                }
                                                disabled={editStatusMutation.isPending}
                                                sx={{
                                                    minHeight: "2.5rem !important",
                                                    height: "2.5rem !important",
                                                    width: { xxs: "100%", md: "auto" },
                                                }}
                                            >
                                                {editStatusMutation.isPending
                                                    ? "Resolving..."
                                                    : "Mark as Resolved"}
                                            </SoftButton>
                                        </SoftBox>
                                    )}
                            </SoftBox>

                            <SoftBox
                                display="flex"
                                flexWrap={{ xxs: "wrap", md: "nowrap" }}
                                flexGrow={1}
                                width={{ xxs: "100%", md: "auto" }}
                                gap={1}
                                justifyContent="flex-end"
                                alignItems="center"
                            >
                                {canViewThread && (
                                    <SoftBox display="flex" flexGrow={{ xxs: 1, md: 0 }}>
                                        <SoftButton
                                            variant="gradient"
                                            size="small"
                                            color="info"
                                            component={Link}
                                            to={`/operations/${selectedInspection.inspection_record_id ||
                                                selectedInspection._id
                                                }`}
                                            sx={{
                                                minHeight: "2.5rem !important",
                                                height: "2.5rem !important",
                                                width: { xxs: "100%", md: "auto" },
                                            }}
                                        >
                                            View Thread
                                        </SoftButton>
                                    </SoftBox>
                                )}

                                {canOperationsDelete && !hasDescendants && (
                                    <SoftBox display="flex" flexGrow={{ xxs: 1, md: 0 }}>
                                        <SoftButton
                                            variant={
                                                clickState === selectedInspection._id
                                                    ? "contained"
                                                    : "outlined"
                                            }
                                            color="error"
                                            onClick={() => handleDelete(selectedInspection._id)}
                                            disabled={deleteMutation.isPending}
                                            sx={{
                                                minHeight: "2.5rem !important",
                                                height: "2.5rem !important",
                                                width: { xxs: "100%", md: "auto" },
                                            }}
                                        >
                                            {clickState === selectedInspection._id ? "Confirm" : "Delete"}
                                        </SoftButton>
                                    </SoftBox>
                                )}
                            </SoftBox>
                        </SoftBox>
                    </Box>
                </SoftBox>
            </SoftBox >
        </Card >
    );
}

InspectionPreviewCard.propTypes = {
    user: PropTypes.object,
    type: PropTypes.string,
    selectedInspection: PropTypes.object,
    setSelectedInspection: PropTypes.func
};

export default InspectionPreviewCard;